import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import FableSheetButton from "@/components/FableSheetButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { BookOpen, Plus, Pencil, Trash2, Music2, Loader2, Feather, ScrollText, Sparkles } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { kind: "conte", title: "", author: "", content: "", audio_url: "", active: true, order: 0 };

export function StoryManager() {
  const [stories, setStories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const audioRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/stories");
      setStories(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...EMPTY, ...s }); setOpen(true); };

  const uploadAudio = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, audio_url: `${BACKEND}${data.url}` }));
      toast.success("Audio envoyé");
    } catch {
      toast.error("Échec de l'envoi de l'audio.");
    } finally { setUploading(false); }
  };

  const generateAudio = async () => {
    if (!form.content.trim()) return toast.error("Ajoutez d'abord le texte à lire.");
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/stories/tts", { text: form.content, voice: "echo" });
      setForm((f) => ({ ...f, audio_url: `${BACKEND}${data.url}` }));
      toast.success("Lecture audio générée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Génération de la voix impossible.");
    } finally { setGenerating(false); }
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Le titre est obligatoire.");
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/stories/${editing.id}`, form);
        toast.success("Contenu mis à jour");
      } else {
        await api.post("/admin/stories", form);
        toast.success("Contenu ajouté");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enregistrement impossible.");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/stories/${id}`);
      toast.success("Contenu supprimé");
      load();
    } catch { toast.error("Suppression impossible."); }
  };

  return (
    <Card className="border-gray-200 mb-8" data-testid="story-manager">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-emerald-700" />
            <h2 className="font-serif-display text-xl font-normal text-gray-800">Contes & Poésie</h2>
          </div>
          <Button onClick={openNew} className="bg-emerald-700 hover:bg-emerald-800 rounded-xl" data-testid="add-story-button">
            <Plus className="w-4 h-4 mr-1" /> Nouveau contenu
          </Button>
        </div>
        {stories.length === 0 ? (
          <p className="text-sm text-gray-400">Aucun conte ni poème pour l'instant.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {stories.map((s) => (
              <div key={s.id} className="border border-gray-200 rounded-2xl p-4 flex items-start gap-3" data-testid={`admin-story-${s.id}`}>
                <span className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                  {s.kind === "poeme" ? <Feather className="w-5 h-5" /> : <ScrollText className="w-5 h-5" />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-gray-800 truncate">{s.title}</p>
                  <p className="text-xs text-gray-500 mb-1">{{ poeme: "Poème", fable: "Fable" }[s.kind] || "Conte"}{s.author ? ` · ${s.author}` : ""}{s.audio_url ? " · 🔊 audio" : ""}{s.active ? "" : " · masqué"}</p>
                  <p className="text-sm text-gray-500 line-clamp-2">{s.content}</p>
                  <div className="flex gap-2 mt-2">
                    <Button variant="outline" size="sm" className="rounded-lg" onClick={() => openEdit(s)} data-testid={`edit-story-${s.id}`}><Pencil className="w-4 h-4" /></Button>
                    <FableSheetButton story={s} compact />
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" className="rounded-lg text-red-600" data-testid={`delete-story-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Supprimer « {s.title} » ?</AlertDialogTitle>
                          <AlertDialogDescription>Cette action est définitive.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annuler</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(s.id)} className="bg-red-600 hover:bg-red-700" data-testid={`confirm-delete-story-${s.id}`}>Supprimer</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="story-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifier le contenu" : "Nouveau conte / poème"}</DialogTitle>
            <DialogDescription>Ajoutez le texte et, si vous le souhaitez, un fichier audio pour la version orale.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger className="mt-1" data-testid="story-kind-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="conte">Conte</SelectItem>
                    <SelectItem value="fable">Fable</SelectItem>
                    <SelectItem value="poeme">Poème</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Auteur (optionnel)</Label>
                <Input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })}
                  placeholder="Ex : Jean de La Fontaine" className="mt-1" data-testid="story-author-input" />
              </div>
            </div>
            <div>
              <Label>Titre</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Titre du conte ou du poème" className="mt-1" data-testid="story-title-input" />
            </div>
            <div>
              <Label>Texte (version écrite)</Label>
              <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder="Écrivez le texte ici…" rows={8} className="mt-1" data-testid="story-content-input" />
            </div>
            {form.kind === "fable" && (
              <div className="grid gap-3 rounded-xl bg-amber-50 border border-amber-200 p-3" data-testid="story-morale-fields">
                <div>
                  <Label>Morale (affichée en grand à la fin)</Label>
                  <Textarea value={form.morale || ""} onChange={(e) => setForm({ ...form, morale: e.target.value })} rows={2} className="mt-1 bg-white" data-testid="story-morale-input" />
                </div>
                <div>
                  <Label>Question pour en parler</Label>
                  <Input value={form.morale_question || ""} onChange={(e) => setForm({ ...form, morale_question: e.target.value })} className="mt-1 bg-white" data-testid="story-morale-question-input" />
                </div>
                {form.quotes?.length > 0 && (
                  <p className="text-xs text-amber-800">Répliques pour « Qui a dit quoi ? » : {form.quotes.map((q) => `« ${q.text} » (${q.animal})`).join(" · ")}</p>
                )}
              </div>
            )}
            <div>
              <Label className="flex items-center gap-1"><Music2 className="w-4 h-4" /> Version orale (audio)</Label>
              <div className="mt-1 flex items-center gap-4">
                {form.audio_url ? (
                  <audio src={form.audio_url} controls className="h-10" data-testid="story-audio-preview" />
                ) : (
                  <span className="text-sm text-gray-400">Aucun audio</span>
                )}
                <input ref={audioRef} type="file" accept="audio/*" hidden onChange={uploadAudio} data-testid="story-audio-input" />
                <Button type="button" variant="outline" className="rounded-lg" disabled={uploading} onClick={() => audioRef.current?.click()} data-testid="story-upload-audio">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Music2 className="w-4 h-4 mr-1" /> Choisir un audio</>}
                </Button>
                <Button type="button" variant="outline" className="rounded-lg border-emerald-300 text-emerald-700" disabled={generating} onClick={generateAudio} data-testid="story-generate-audio">
                  {generating ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Génération…</> : <><Sparkles className="w-4 h-4 mr-1" /> Générer la lecture</>}
                </Button>
                {form.audio_url && (
                  <button type="button" onClick={() => setForm({ ...form, audio_url: "" })} className="text-sm text-red-500" data-testid="story-audio-remove">Retirer</button>
                )}
              </div>
            </div>
            <label className="flex items-center gap-3 pt-1 cursor-pointer border-t mt-2 pt-4">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="story-active-switch" />
              <span className="text-sm text-gray-700">Visible par les résidents et les familles</span>
            </label>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-story-button">
              {saving ? "Enregistrement..." : editing ? "Enregistrer" : "Ajouter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
